import { useEffect, useMemo, useRef, useState } from "react"
import { type Filter, type Query } from "@cubejs-client/core"

import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage"
import { Button } from "~/ui-rtk/components/ui/controls"
import { H3 } from "~/ui-rtk/components/ui/typography"
import { SOURCES_MAP } from "~/ui-rtk/constants/sources"
import {
  convertNumber,
  type ConvertNumberType,
} from "~/ui-rtk/utils/number-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { resolveRowAssets } from "~/ui-rtk/utils/resolve-row-assets"
import Alert from "~/ui-rtk/components/ui/common/Alert"
import { useBulkGetTagAssignmentsQuery } from "~/ui-rtk/api/tagAssignmentsApi"
import { useGetMetaAdMediaMutation } from "~/ui-rtk/api/paidMediaApi"
import { UNTAGGED_LABEL, countPendingSync } from "../connect"

// Everything arriving here comes through the drawer's redux state, so every
// prop must stay serializable (no dates, no functions).
export type TTaxonomyGroupAdsProps = {
  /** Section title, e.g. "Hook". */
  title: string
  /** Prefix-stripped dimension key the clicked row grouped by, e.g. "hook". */
  dimension: string
  /**
   * Tag category slug behind that dimension, e.g. "hook" or a custom
   * "talent". Custom categories all group by the same generic dimension, so
   * the slug — not the dimension — is what the live-assignment lookup keys on.
   */
  tagSlug?: string
  /** The clicked row's dimension value, e.g. "curiosity". */
  value: string
  /** Cube view prefix, e.g. "creative_taxonomy_report". */
  prefix: string
  /** Section + page-level filters the clicked row was computed under. */
  filters?: Filter[]
  dateRange: [string, string]
  /** Section metric columns (plain {key, header, type} objects). */
  metricColumns?: TableVisualizationColumn[]
}

const AD_LIMIT = 500

// Cards revealed initially / added per "Show more" click.
const PAGE_SIZE = 24

/** A Meta ad's creative read live from Meta (signed fbcdn URL, kept in memory only). */
type TLiveMedia = { url: string; isVideo: boolean }

const ASSET_FIELDS = {
  sourceTypeKey: "media_channel",
  externalRefIdKey: "ad_id",
  urlKey: "md_image_url",
}

// One ad, rendered in the Creative Reports card language: square creative
// preview with the channel icon overlaid, ad name, then the section's metric
// rows. A component (not inline markup in a table cell) so it can subscribe to
// the asset cache and re-render itself when the bulk resolve lands.
function TaxonomyAdCard({
  row,
  metricColumns,
  live,
  isMediaLoading = false,
}: {
  row: TMetric
  metricColumns: TableVisualizationColumn[]
  /** Meta ads: the creative straight from Meta, ahead of the asset cache. */
  live?: TLiveMedia
  /** The ad's Meta media request is in flight: show a loader over the preview. */
  isMediaLoading?: boolean
}) {
  const { isEnabled: isDemoEnabled } = useDemoMode()

  const adId = row.ad_id ? String(row.ad_id) : undefined
  const mediaChannel = String(row.media_channel ?? "")
  const adName = String(row.ad_name ?? "—")

  const cacheKey = `${mapMediaChannelToSourceType(mediaChannel)}:${adId}`
  const cachedUrl = useAppSelector(state =>
    adId ? state.assetCacheSlice.urls[cacheKey] : undefined,
  )
  const src =
    live?.url ??
    cachedUrl ??
    (row.md_image_url ? String(row.md_image_url) : undefined)
  const isVideo = !!live?.isVideo

  // Pulses until the <img> actually paints — covers both "bulk resolve still
  // in flight" (no src yet) and "src known but bytes still downloading".
  const [isLoaded, setIsLoaded] = useState(false)
  useEffect(() => {
    setIsLoaded(false)
  }, [src])

  // An ad with no resolvable asset and no platform URL would otherwise pulse
  // forever — settle into a static placeholder box instead.
  const [gaveUp, setGaveUp] = useState(false)
  useEffect(() => {
    if (src) return
    const timer = setTimeout(() => setGaveUp(true), 10_000)
    return () => clearTimeout(timer)
  }, [src])

  const source =
    SOURCES_MAP[mediaChannel.toUpperCase() as keyof typeof SOURCES_MAP]

  return (
    <Card className="p-0 rounded-system-xl relative bg-system-blue-800 border border-basic-blue/40 hover:border-brand/60 transition-all duration-300 flex flex-col overflow-hidden">
      {/* Creative preview */}
      <div className="relative p-system-sm">
        <div className="relative aspect-square w-full rounded-system-lg overflow-hidden bg-basic-black/50 border border-basic-blue/20">
          {/* A <video> never fires the img onLoad, so it skips the pulse. */}
          {!isVideo && !isLoaded && !(gaveUp && !src) && (
            <div className="absolute inset-0 animate-pulse bg-system-blue-700" />
          )}
          {src && (
            <Image
              src={src}
              alt={adName}
              // Meta videos play right in the card.
              allowVideoPlayback={isVideo}
              mediaType={isVideo ? "video" : undefined}
              classes={{
                wrapper: cn("w-full h-full", isDemoEnabled && "blur-md"),
                image: "w-full h-full object-cover",
              }}
              onLoad={() => setIsLoaded(true)}
            />
          )}
          {isMediaLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-basic-black/50">
              <Loader size={32} />
            </div>
          )}
        </div>
        {source && (
          <img
            src={source.icon}
            alt={source.name}
            title={source.name}
            className="absolute left-system-md top-system-md h-8 w-8 object-contain"
          />
        )}
      </div>

      {/* Name + metric rows */}
      <div className="p-system-sm pt-0 flex flex-col gap-system-xs">
        <H3
          className={cn(
            "text-white font-bold text-base leading-tight truncate",
            isDemoEnabled && "blur-md",
          )}
        >
          <span title={adName}>{adName}</span>
        </H3>
        <div className="bg-basic-black/80 rounded-system-lg border border-basic-blue/40 divide-y divide-basic-blue/30">
          {metricColumns.map(col => (
            <div
              key={col.key}
              className="flex justify-between items-center py-system-xs px-system-sm"
            >
              <span className="text-sm text-gray-200 font-medium">
                {col.header}
              </span>
              <span className="text-sm font-bold text-white">
                {convertNumber(
                  (col.type ?? "number") as ConvertNumberType,
                  Number(row[col.key] ?? 0),
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
}

/**
 * Drawer content for a taxonomy breakdown row: every ad in the clicked group
 * (dimension = value, within the same filters + date range as the report) as
 * Creative Reports-style cards, sorted by spend.
 */
export default function TaxonomyGroupAds({
  title,
  dimension,
  tagSlug,
  value,
  prefix,
  filters,
  dateRange,
  metricColumns,
}: TTaxonomyGroupAdsProps) {
  const isUntagged = value === UNTAGGED_LABEL

  // Per-ad cards make a per-group count metric meaningless (always 1).
  const adMetricColumns = useMemo(
    () => (metricColumns ?? []).filter(({ key }) => key !== "ad_count"),
    [metricColumns],
  )

  const query = useMemo<Query>(() => {
    const m = (name: string) => `${prefix}.${name}`
    const measures = adMetricColumns.map(({ key }) => m(key))
    return {
      dimensions: [
        m("ad_id"),
        m("ad_name"),
        m("md_image_url"),
        m("media_channel"),
      ],
      measures,
      filters: [
        ...(filters ?? []),
        // The untagged bucket groups rows where the dimension is NULL, so it
        // needs the unary `notSet` operator — an `equals` against the display
        // label would match nothing and the drawer would come back empty.
        isUntagged
          ? { member: m(dimension), operator: "notSet" }
          : { member: m(dimension), operator: "equals", values: [value] },
      ],
      timeDimensions: [{ dimension: m("md_date"), dateRange }],
      ...(measures.length > 0 ? { order: { [measures[0]]: "desc" } } : {}),
      limit: AD_LIMIT,
    }
  }, [
    prefix,
    adMetricColumns,
    filters,
    dimension,
    value,
    dateRange,
    isUntagged,
  ])

  const { resultSet, isLoading, error } = useCubeQuery(query)

  const rows = useMemo<TMetric[]>(() => {
    if (!resultSet) return []
    return resultSet.rawData().map((item, idx) => {
      const row: Record<string, unknown> = { id: `taxonomy-group-ad-${idx}` }
      Object.keys(item).forEach(key => {
        row[key.replace(/^[^.]+\./, "")] = item[key]
      })
      return row as TMetric
    })
  }, [resultSet])

  // This report reads creative tags from the warehouse, which is rebuilt on the
  // pipeline's schedule, while the tagging UI writes to Postgres immediately.
  // Anything tagged since the last run still shows up here as untagged, which
  // silently overstates the gap — on 2026-08-19, 208 of 294 "untagged" ads had
  // in fact been tagged that morning. Ask the app for the live assignments so
  // the drawer can say so rather than letting the stale count stand.
  const adIds = useMemo(
    () => rows.map(row => String(row.ad_id ?? "")).filter(Boolean),
    [rows],
  )

  const { data: liveTags } = useBulkGetTagAssignmentsQuery(
    { itemType: "ad", itemIds: adIds },
    { skip: !isUntagged || adIds.length === 0 },
  )

  const pendingSyncCount = useMemo(
    () => countPendingSync(liveTags, tagSlug),
    [liveTags, tagSlug],
  )

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [dimension, value])

  const visibleRows = useMemo(
    () => rows.slice(0, visibleCount),
    [rows, visibleCount],
  )

  // Bulk-resolve preview assets by ad id — ONE request per revealed batch;
  // cards read the resolved URLs from the asset-cache slice and never fire
  // their own resolve requests. Meta ads load their creative straight from
  // Meta (playable video when Meta gives a source); the asset cache stays the
  // source for other channels and the fallback for ads Meta can't serve.
  const dispatch = useAppDispatch()
  const cachedAssetUrls = useAppSelector(state => state.assetCacheSlice.urls)
  const [getMetaAdMedia] = useGetMetaAdMediaMutation()
  const [metaMedia, setMetaMedia] = useState<Record<string, TLiveMedia>>({})
  const requestedMetaAdIdsRef = useRef(new Set<string>())
  const [pendingMetaAdIds, setPendingMetaAdIds] = useState<ReadonlySet<string>>(
    new Set(),
  )
  useEffect(() => {
    if (!visibleRows.length) return
    const ctx = { dispatch, cachedAssetUrls }
    const isMeta = (row: TMetric) =>
      mapMediaChannelToSourceType(String(row.media_channel ?? "")) ===
        "FACEBOOK_AD" && !!row.ad_id
    void resolveRowAssets(
      visibleRows.filter(row => !isMeta(row)),
      ASSET_FIELDS,
      ctx,
    )

    const metaRows = visibleRows.filter(
      row =>
        isMeta(row) && !requestedMetaAdIdsRef.current.has(String(row.ad_id)),
    )
    if (metaRows.length === 0) return
    const adIds = [...new Set(metaRows.map(row => String(row.ad_id)))]
    for (const id of adIds) requestedMetaAdIdsRef.current.add(id)
    setPendingMetaAdIds(prev => new Set([...prev, ...adIds]))
    void getMetaAdMedia({ adIds })
      .unwrap()
      .then(media => {
        const found: Record<string, TLiveMedia> = {}
        for (const id of adIds) {
          const m = media[id]
          const url = m?.videoUrl ?? m?.thumbnailUrl ?? m?.imageUrl
          if (url) found[id] = { url, isVideo: !!m?.videoUrl }
        }
        setMetaMedia(prev => ({ ...prev, ...found }))
        void resolveRowAssets(
          metaRows.filter(row => !found[String(row.ad_id)]),
          ASSET_FIELDS,
          ctx,
        )
      })
      .catch(() => resolveRowAssets(metaRows, ASSET_FIELDS, ctx))
      .finally(() =>
        setPendingMetaAdIds(prev => {
          const next = new Set(prev)
          for (const id of adIds) next.delete(id)
          return next
        }),
      )
  }, [visibleRows])

  return (
    <div className="p-system-md flex flex-col gap-system-md min-w-0">
      <div>
        <H3 className="text-white font-bold text-lg">
          {title}: {value}
        </H3>
        <p className="text-sm text-system-gray-400 mt-0.5">
          Ads in this group for {dateRange[0]} – {dateRange[1]}
          {rows.length > 0 &&
            ` · ${rows.length}${rows.length === AD_LIMIT ? "+" : ""} ads`}
        </p>
      </div>

      {pendingSyncCount > 0 && (
        <Alert
          variant="warning"
          size="sm"
          injectable={false}
          title="Some of these are already tagged"
          content={`${pendingSyncCount} of the ${rows.length} ads below have a ${title.toLowerCase()} tag saved in the app, but the creative-tag data behind this report is refreshed by the nightly pipeline — they will drop out of this group after the next run. The untagged total above is overstated by that amount.`}
        />
      )}

      {error ? (
        <div className="py-10 text-error text-sm text-center">
          {error.message || "Failed to load ads"}
        </div>
      ) : isLoading && rows.length === 0 ? (
        <div className="py-10 flex justify-center">
          <Loader size={40} />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-system-gray-400 text-sm text-center">
          No ads found for this group in the selected time range.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-system-md">
            {visibleRows.map(row => (
              <TaxonomyAdCard
                key={String(row.id)}
                row={row}
                metricColumns={adMetricColumns}
                live={metaMedia[String(row.ad_id ?? "")]}
                isMediaLoading={pendingMetaAdIds.has(String(row.ad_id ?? ""))}
              />
            ))}
          </div>
          {rows.length > visibleCount && (
            <div className="flex justify-center">
              <Button
                size="sm"
                variant={{ variant: "outlined" }}
                onClick={() => setVisibleCount(count => count + PAGE_SIZE)}
              >
                Show more ({rows.length - visibleCount} remaining)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
